/**
 * CSV export utilities for Class Insights
 * Handles CSV generation for all sections
 */

import {
  transformCommentBanksToCSV,
  transformSuccessCriteriaToCSV,
  transformSummaryToCSV,
  transformRubricsToCSV,
  transformShortAnswerToCSV,
} from './dataTransformers';
import { SECTIONS } from './fileNaming';

/**
 * Convert array data to CSV string
 * @param {Array} data - 2D array of data
 * @returns {string} CSV formatted string
 */
function arrayToCSV(data) {
  return data
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = String(cell ?? '');
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(',')
    )
    .join('\n');
}

/**
 * Export comment banks data to CSV
 * @param {Array} commentBankStats - Comment bank statistics
 * @returns {string} CSV string
 */
export function exportCommentBanksToCSV(commentBankStats) {
  const data = transformCommentBanksToCSV(commentBankStats);
  return arrayToCSV(data);
}

/**
 * Export success criteria data to CSV
 * @param {Array} successCriteriaStats - Success criteria statistics
 * @returns {string} CSV string
 */
export function exportSuccessCriteriaToCSV(successCriteriaStats) {
  const data = transformSuccessCriteriaToCSV(successCriteriaStats);
  return arrayToCSV(data);
}

/**
 * Export summary/KPI data to CSV
 * @param {Object} submissionsStats - Submissions statistics
 * @returns {string} CSV string
 */
export function exportSummaryToCSV(submissionsStats) {
  const data = transformSummaryToCSV(submissionsStats);
  return arrayToCSV(data);
}

/**
 * Export rubrics data to CSV
 * @param {Array} rubricsStats - Rubrics statistics
 * @returns {string} CSV string
 */
export function exportRubricsToCSV(rubricsStats) {
  const data = transformRubricsToCSV(rubricsStats);
  return arrayToCSV(data);
}

/**
 * Export short answer data to CSV
 * @param {Array} shortAnswerStats - Short answer statistics
 * @returns {string} CSV string
 */
export function exportShortAnswerToCSV(shortAnswerStats) {
  const data = transformShortAnswerToCSV(shortAnswerStats);
  return arrayToCSV(data);
}

/**
 * Export full class report to CSV (all sections combined)
 * @param {Object} allData - Object containing all section data
 * @param {Object} allData.submissionsStats - Summary statistics
 * @param {Array} allData.commentBankStats - Comment bank statistics
 * @param {Array} allData.successCriteriaStats - Success criteria statistics
 * @param {Array} allData.rubricsStats - Rubrics statistics (optional)
 * @param {Array} allData.shortAnswerStats - Short answer statistics (optional)
 * @returns {string} Combined CSV string
 */
export function exportFullReportToCSV(allData) {
  // Helper function to check if data has actual content
  const hasCommentBankData = Array.isArray(allData.commentBankStats) && allData.commentBankStats.length > 0;
  const hasSuccessCriteriaData = Array.isArray(allData.successCriteriaStats) && allData.successCriteriaStats.length > 0;
  const hasRubricsData = Array.isArray(allData.rubricsStats) && allData.rubricsStats.length > 0;
  const hasShortAnswerData = allData.shortAnswerStats &&
    ((Array.isArray(allData.shortAnswerStats.tasks) && allData.shortAnswerStats.tasks.length > 0) ||
     (Array.isArray(allData.shortAnswerStats) && allData.shortAnswerStats.length > 0));

  // Calculate detailed metrics
  const totalSuccessCriteriaObjectives = (allData.successCriteriaStats || []).reduce(
    (sum, sc) => sum + (sc.objectives?.length || 0), 0
  );
  const totalRubricRows = (allData.rubricsStats || []).reduce(
    (sum, task) => sum + (task.stats?.length || 0), 0
  );

  console.log('📊 [CSV Export] Exporting COMPLETE full report with ALL data:', {
    hasSubmissions: !!allData.submissionsStats,
    commentBanks: {
      banks: allData.commentBankStats?.length || 0,
      totalSuggestions: (allData.commentBankStats || []).reduce(
        (sum, bank) => sum + (bank.suggestions?.length || 0), 0
      ),
    },
    successCriteria: {
      criteria: allData.successCriteriaStats?.length || 0,
      objectives: totalSuccessCriteriaObjectives,
    },
    rubrics: {
      tasks: allData.rubricsStats?.length || 0,
      rows: totalRubricRows,
    },
    shortAnswer: {
      stats: allData.shortAnswerStats?.tasks?.length ?? allData.shortAnswerStats?.length ?? 0,
    },
    willIncludeCommentBanks: hasCommentBankData,
    willIncludeSuccessCriteria: hasSuccessCriteriaData,
    willIncludeRubrics: hasRubricsData,
    willIncludeShortAnswer: hasShortAnswerData,
  });

  const sections = [];

  // Summary section - always include if submissions data exists
  if (allData.submissionsStats) {
    sections.push('=== SUMMARY ===');
    sections.push(exportSummaryToCSV(allData.submissionsStats));
    sections.push('');
  }

  // Comment Banks section - only include if data exists
  if (hasCommentBankData) {
    sections.push('=== COMMENT BANKS ===');
    const commentBanksCsv = exportCommentBanksToCSV(allData.commentBankStats);
    const rowCount = commentBanksCsv.split('\n').length - 1;
    console.log('📊 [CSV Export] Comment Banks: exported', rowCount, 'rows');
    sections.push(commentBanksCsv);
    sections.push('');
  } else {
    console.log('⏭️ [CSV Export] Skipping Comment Banks section - no data available');
  }

  // Success Criteria section - only include if data exists
  if (hasSuccessCriteriaData) {
    sections.push('=== SUCCESS CRITERIA ===');
    const scCsv = exportSuccessCriteriaToCSV(allData.successCriteriaStats);
    const rowCount = scCsv.split('\n').length - 1; // Subtract header row
    const totalObjectives = (allData.successCriteriaStats || []).reduce(
      (sum, sc) => sum + (sc.objectives?.length || 0), 0
    );
    console.log('📊 [CSV Export] Success Criteria: exported', rowCount, 'data rows from', allData.successCriteriaStats.length, 'criteria with', totalObjectives, 'total objectives');
    sections.push(scCsv);
    sections.push('');
  } else {
    console.log('⏭️ [CSV Export] Skipping Success Criteria section - no data available');
  }

  // Rubrics section - only include if data exists
  if (hasRubricsData) {
    sections.push('=== RUBRICS ===');
    const rubricsCsv = exportRubricsToCSV(allData.rubricsStats);
    const rowCount = rubricsCsv.split('\n').length - 1;
    console.log('📊 [CSV Export] Rubrics: exported', rowCount, 'rows');
    sections.push(rubricsCsv);
    sections.push('');
  } else {
    console.log('⏭️ [CSV Export] Skipping Rubrics section - no data available');
  }

  // Short Answer section - only include if data exists
  if (hasShortAnswerData) {
    sections.push('=== SHORT ANSWER ===');
    const shortAnswerCsv = exportShortAnswerToCSV(allData.shortAnswerStats);
    const rowCount = shortAnswerCsv.split('\n').length - 1;
    console.log('📊 [CSV Export] Short Answer: exported', rowCount, 'rows');
    sections.push(shortAnswerCsv);
    sections.push('');
  } else {
    console.log('⏭️ [CSV Export] Skipping Short Answer section - no data available');
  }

  const includedSections = [];
  if (allData.submissionsStats) includedSections.push('Summary');
  if (hasCommentBankData) includedSections.push('Comment Banks');
  if (hasSuccessCriteriaData) includedSections.push('Success Criteria');
  if (hasRubricsData) includedSections.push('Rubrics');
  if (hasShortAnswerData) includedSections.push('Short Answer');

  console.log('✅ [CSV Export] Full report CSV generated with sections:', includedSections.join(', '));
  return sections.join('\n');
}

/**
 * Trigger browser download of CSV file
 * @param {string} csvContent - CSV string content
 * @param {string} filename - Desired filename
 */
export function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export section data to CSV based on section type
 * @param {string} section - Section type (from SECTIONS constant)
 * @param {*} data - Section data
 * @param {string} filename - Desired filename
 */
export function exportSectionToCSV(section, data, filename) {
  let csvContent;

  switch (section) {
    case SECTIONS.COMMENT_BANKS:
      csvContent = exportCommentBanksToCSV(data);
      break;
    case SECTIONS.SUCCESS_CRITERIA:
      csvContent = exportSuccessCriteriaToCSV(data);
      break;
    case SECTIONS.SUMMARY:
      csvContent = exportSummaryToCSV(data);
      break;
    case SECTIONS.RUBRICS:
      csvContent = exportRubricsToCSV(data);
      break;
    case SECTIONS.SHORT_ANSWER:
      csvContent = exportShortAnswerToCSV(data);
      break;
    default:
      throw new Error(`Unknown section type: ${section}`);
  }

  downloadCSV(csvContent, filename);
}
